import { FC } from "preact/compat";
import { useEffect, useMemo } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { addYears, format, startOfYear } from "date-fns";
import { useAppTime } from "~/hooks";
import { MONTH_NAMES } from "~/utils";
import WheelInput from "./WheelInput";
import { WheelDateInputProps } from "./interfaces";

const DEFAULT_MIN_DATE = startOfYear(addYears(new Date(), -20));

const clampMonth = (
    month: number,
    year: number,
    min: Date | null,
    max: Date | null,
) => {
    const minYear = min?.getFullYear() ?? 0;
    const maxYear = max?.getFullYear() ?? 0;
    const minMonth = min?.getMonth() ?? 0;
    const maxMonth = max?.getMonth() ?? 0;

    if (year === minYear && month < minMonth) {
        return minMonth;
    }

    if (year === maxYear && month > maxMonth) {
        return maxMonth;
    }

    return month;
};

const clampDay = (
    day: number,
    month: number,
    year: number,
    min: Date | null,
    max: Date | null,
) => {
    const minYear = min?.getFullYear() ?? 0;
    const maxYear = max?.getFullYear() ?? 0;
    const minMonth = min?.getMonth() ?? 0;
    const maxMonth = max?.getMonth() ?? 0;
    const minDay = min?.getDate() ?? 1;
    const maxDay = max?.getDate() ?? 31;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    if (day < 1) {
        return 1;
    }

    if (year === minYear && month === minMonth && day < minDay) {
        return minDay;
    }

    if (year === maxYear && month === maxMonth && day > maxDay) {
        return maxDay;
    }

    if (day > daysInMonth) {
        return daysInMonth;
    }

    return day;
};

const WheelDateInput: FC<WheelDateInputProps> = ({
    show,
    value,
    onChange,
    min = DEFAULT_MIN_DATE,
    max = "today",
    className,
}) => {
    const values = useSignal([
        value.getMonth().toString(),
        value.getDate().toString(),
        value.getFullYear().toString(),
    ]);
    const yearSignal = useSignal(value.getFullYear().toString());
    const monthSignal = useSignal(value.getMonth().toString());
    const today = useAppTime("day", {
        skip: min !== "today" && max !== "today",
    });
    const years = useComputed(() => {
        const td = today.value;
        const md = min === "today" ? td : min;
        const xd = max === "today" ? td : max;
        const minYear = md?.getFullYear() ?? 0;
        const maxYear = xd?.getFullYear() ?? new Date().getFullYear();
        const minMonth = md?.getMonth() ?? 0;
        const maxMonth = xd?.getMonth() ?? 11;
        const result = [];

        for (let i = minYear; i <= maxYear; ++i) {
            if (i === minYear && minMonth > value.getMonth()) {
                continue;
            }

            if (i === maxYear && maxMonth < value.getMonth()) {
                continue;
            }

            result.push({
                label: i.toString(),
                value: i.toString(),
            });
        }

        return result;
    });
    const months = useComputed(() => {
        const td = today.value;
        const md = min === "today" ? td : min;
        const xd = max === "today" ? td : max;
        const year = parseInt(yearSignal.value, 10);
        const minYear = md?.getFullYear() ?? 0;
        const maxYear = xd?.getFullYear() ?? new Date().getFullYear();
        const minMonth = md?.getMonth() ?? 0;
        const maxMonth = xd?.getMonth() ?? 11;
        const result = [];

        for (let i = 0; i < MONTH_NAMES.length; ++i) {
            if (year === minYear && i < minMonth) {
                continue;
            }

            if (year === maxYear && i > maxMonth) {
                continue;
            }

            result.push({
                label: MONTH_NAMES[i],
                value: i.toString(),
            });
        }

        return result;
    });
    const days = useComputed(() => {
        const td = today.value;
        const md = min === "today" ? td : min;
        const xd = max === "today" ? td : max;
        const year = parseInt(yearSignal.value, 10);
        const month = parseInt(monthSignal.value, 10);
        const minYear = md?.getFullYear() ?? 0;
        const maxYear = xd?.getFullYear() ?? new Date().getFullYear();
        const minMonth = md?.getMonth() ?? 0;
        const maxMonth = xd?.getMonth() ?? 11;
        const minDay = md?.getDate() ?? 1;
        const maxDay = xd?.getDate() ?? 31;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const result = [];

        for (let i = 1; i <= daysInMonth; ++i) {
            if (year === minYear && month === minMonth && i < minDay) {
                continue;
            }

            if (year === maxYear && month === maxMonth && i > maxDay) {
                continue;
            }

            result.push({
                label: i.toString().padStart(2, "0"),
                value: i.toString(),
            });
        }

        return result;
    });
    const wheels = useMemo(
        () => [
            { label: "Month", choices: months },
            { label: "Day", choices: days },
            { label: "Year", choices: years },
        ],
        [months, years, days],
    );
    const previousValue = [
        value.getMonth().toString(),
        value.getDate().toString(),
        value.getFullYear().toString(),
    ];

    useSignalEffect(() => {
        const [m, , y] = values.value;

        if (monthSignal.peek() !== m) {
            monthSignal.value = m;
        }

        if (yearSignal.peek() !== y) {
            yearSignal.value = y;
        }
    });

    useEffect(() => {
        const d = value.getDate().toString();
        const m = value.getMonth().toString();
        const y = value.getFullYear().toString();
        const newValue = [m, d, y];

        if (newValue.some((v, idx) => v !== values.value[idx])) {
            values.value = newValue;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [format(value, "dd.MM.yyyy")]);

    const handleChange = (newValues: string[]) => {
        const year = parseInt(newValues[2], 10);
        const month = parseInt(newValues[0], 10);
        const day = parseInt(newValues[1], 10);
        const newDate = new Date(year, month, day);

        onChange(newDate);
    };

    return (
        <WheelInput
            show={show}
            wheels={wheels}
            value={previousValue}
            valueSignal={values}
            className={className}
            onChange={handleChange}
            renderLabel={([m, d, y]) =>
                `${d.padStart(2, "0")}.${(parseInt(m, 10) + 1).toString().padStart(2, "0")}.${y}`
            }
            transform={([m, d, y]) => {
                const md = min === "today" ? today.peek() : min;
                const xd = max === "today" ? today.peek() : max;
                const year = parseInt(y, 10);
                const month = clampMonth(parseInt(m, 10), year, md, xd);
                const day = clampDay(parseInt(d, 10), month, year, md, xd);

                return [month.toString(), day.toString(), year.toString()];
            }}
        />
    );
};

export default WheelDateInput;
